'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Plus, ScrollText, Search, User } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/components/context/auth-context';
import useRedirect from '@/libs/hooks/use-redirect';
import { usePathname, useRouter } from 'next/navigation';
import styles from './styles.module.scss';
import Container from '@/components/primitives/container';
import Typography from '@/components/primitives/typography';
import Avatar from '@/components/primitives/avatar';
import { useAsyncEffect } from '@/hooks/use-async-effect';

interface BottomNavigationProps {
  children?: React.ReactNode;
}

interface BottomNavigationProviderProps {
  visible: boolean;
  setVisible: (visible: boolean | ((prev: boolean) => boolean)) => void,
}

interface BottomNavigationItemProps {
  icon: React.ReactNode;
  text?: string;
  selected?: boolean;
}

type NavigationKey = 'feed' | 'nearby' | 'search' | 'account';

const BottomNavigationContext = createContext<BottomNavigationProviderProps>({
  visible: true,
  setVisible: (_: boolean | ((prev: boolean) => boolean)) => {
  },
});

const iconProps = {
  width: 24,
  height: 24,
  strokeWidth: 1,
};

const NavigationItem = (props: BottomNavigationItemProps) => (<div
  className={`${styles.navigationItem} ${props.selected ? styles.selected : ''}`}>
  {props.icon}
  {props.text ? <Typography className={styles.navigationText} size="extra-small">{props.text}</Typography> : ''}
</div>);

const BottomNavigationProvider = (props: BottomNavigationProps) => {
  const { children } = props;
  const [visible, setVisible] = React.useState(true);
  const [height, setHeight] = useState(0);
  const auth = useAuth();
  const router = useRouter();
  const redirect = useRedirect();
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  // 디자인 기준: 홈·주변·검색은 해당 탭, 내 계정 화면만 프로필 탭을 선택한다.
  // 피드 상세·해시태그·설정처럼 탭에 속하지 않는 화면과 다른 사람의 프로필에서는 아무 탭도 선택하지 않는다.
  const myAccountName = auth.me?.activeAccount?.name;
  const activeNav = useMemo<NavigationKey | null>(() => {
    if (pathname === '/') return 'feed';
    if (pathname.startsWith('/nearby')) return 'nearby';
    if (pathname.startsWith('/search')) return 'search';
    if (myAccountName && decodeURIComponent(pathname) === `/accounts/${myAccountName}`) return 'account';
    return null;
  }, [pathname, myAccountName]);
  const currentProps = (key: NavigationKey) => (activeNav === key ? { 'aria-current': 'page' as const } : {});

  // 로그인 버튼 클릭 시 현재 URL 저장
  const handleLoginClick = useCallback(() => {
    // 현재 URL 저장
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/auth/sign-in') {
      redirect.set(currentPath);
    }
    router.push('/auth/sign-in');
  }, [redirect, router]);

  useEffect(() => {
    if (!ref.current) return;
    setHeight(ref.current.scrollHeight);
  }, [visible, ref]);

  return <BottomNavigationContext.Provider value={{
    visible,
    setVisible,
  }}>
    <div className={styles.container} style={{ '--bottom-navigation-height': `${height}px` } as React.CSSProperties}>
      {children}
    </div>
    <nav ref={ref} className={styles.nav}>
      <div className={`${styles.navigation} ${visible ? styles.visible : styles.hidden}`}>
        <Container rootClassName={`${styles.navigationItemsRoot} ${styles.navigationBorder}`}
                   className={styles.navigationItems} blur={true}>
          <Link href="/" className={styles.navigationLink} {...currentProps('feed')}>
            <NavigationItem icon={<ScrollText {...iconProps} />} text="피드" selected={activeNav === 'feed'} />
          </Link>
          <Link href="/nearby" className={styles.navigationLink} {...currentProps('nearby')}>
            <NavigationItem icon={<MapPin {...iconProps} />} text="주변" selected={activeNav === 'nearby'} />
          </Link>
          <Link href="/search" className={styles.navigationLink} {...currentProps('search')}>
            <NavigationItem icon={<Search {...iconProps} />} text="검색" selected={activeNav === 'search'} />
          </Link>
          {auth.me ?
            <Link href={`/accounts/${auth.me.activeAccount?.name}`} className={styles.navigationLink}
                  {...currentProps('account')}>
              <NavigationItem
                selected={activeNav === 'account'}
                icon={
                  <Avatar
                    account={auth.me.activeAccount}
                    size={24}
                    showBadge={true}
                    variant="w96"
                  />
                }
                text={auth.me.activeAccount ? auth.me.activeAccount?.nickname || `@${auth.me.activeAccount?.name}` : '계정없음'}
              />
            </Link> :
            <button type="button" className={styles.navigationLink} onClick={handleLoginClick}>
              <NavigationItem icon={<User {...iconProps} />} text="로그인" />
            </button>
          }
        </Container>
        <Link href="/feeds/write" aria-label="피드 작성" className={styles.writeLink}>
          <Container rootClassName={styles.navigationBorder} className={styles.writeButton} blur={true}>
            <NavigationItem icon={<Plus {...iconProps} />} />
          </Container>
        </Link>
      </div>

      <div className={styles.socks} />
    </nav>

  </BottomNavigationContext.Provider>;
};

export const useBottomNavigation = (visible?: boolean) => {
  const context = useContext(BottomNavigationContext);
  const [visibleState, setVisibleState] = useState(visible);

  useEffect(() => {
    setVisibleState(visible);
  }, [visible]);

  useEffect(() => {
    if (visibleState !== undefined) {
      context.setVisible(visibleState);
    }
  }, [visibleState, context]);

  return {
    ...context,
  };
};

export default BottomNavigationProvider;
